const card = (type = 'IMAGE') => ({ headerType: type, mediaHandle: '', mediaName: '', body: '', examples: {}, buttons: [{ type: type === 'PRODUCT' ? 'SPM' : 'URL', text: type === 'PRODUCT' ? 'View' : '', url: '' }] });
const button = (type, text = '') => ({ type, text, url: '', phone_number: '', example: '' });

export function createMarketingForm(format = 'custom') {
  const form = {
    format, name: '', language: 'en_US', body: '', examples: {},
    headerType: 'NONE', headerText: '', headerExamples: {}, mediaHandle: '', mediaName: '',
    footer: '', buttons: [], offerText: 'Special offer', offerExpires: true,
    cards: [],
  };
  switch (format) {
    case 'coupon': form.buttons = [button('COPY_CODE')]; break;
    case 'limited_offer': form.headerType = 'IMAGE'; form.buttons = [button('COPY_CODE'), button('URL', 'Shop now')]; break;
    case 'location': form.headerType = 'LOCATION'; break;
    case 'media_carousel': form.cards = [card(), card()]; break;
    case 'catalog': form.buttons = [button('CATALOG', 'View catalog')]; break;
    case 'multi_product': form.headerType = 'TEXT'; form.headerText = 'Featured items'; form.buttons = [button('MPM', 'View items')]; break;
    case 'product_carousel': form.cards = [card('PRODUCT'), card('PRODUCT')]; break;
    case 'single_product': form.headerType = 'PRODUCT'; form.buttons = [button('SPM', 'View')]; break;
    default: break;
  }
  return form;
}

export function placeholders(text) {
  return [...new Set([...String(text || '').matchAll(/\{\{\s*([a-zA-Z_][a-zA-Z_0-9]*|\d+)\s*\}\}/g)].map(match => match[1]))];
}

function textExample(text, samples, kind, errors, label) {
  const keys = placeholders(text);
  if (!keys.length) return null;
  const numeric = keys.every(key => /^\d+$/.test(key));
  const named = keys.every(key => /^[a-z][a-z_0-9]*$/.test(key));
  if (!numeric && !named) errors.push(`${label}: use only numbered variables or lowercase named variables.`);
  if (numeric && keys.some((key, index) => Number(key) !== index + 1)) errors.push(`${label}: number variables in order from {{1}}.`);
  if (keys.some(key => !String(samples?.[key] || '').trim())) errors.push(`${label}: enter a sample for each variable.`);
  if (named) return { [`${kind}_text_named_params`]: keys.map(param_name => ({ param_name, example: samples?.[param_name] || '' })) };
  return { [`${kind}_text`]: kind === 'body' ? [keys.map(key => samples?.[key] || '')] : keys.map(key => samples?.[key] || '') };
}

function makeButtons(items, errors, label) {
  return items.map((item, index) => {
    const title = `${label} button ${index + 1}`;
    const type = item.type;
    if (type === 'COPY_CODE') {
      if (!item.example || item.example.length > 20) errors.push(`${title}: enter a sample code (up to 20 characters).`);
      return { type, example: item.example || '' };
    }
    if (!String(item.text || '').trim() || item.text.length > 25) errors.push(`${title}: enter a label up to 25 characters.`);
    const result = { type, text: (item.text || '').trim() };
    if (type === 'URL') {
      if (!/^https:\/\//i.test(item.url || '')) errors.push(`${title}: enter an HTTPS website URL.`);
      result.url = item.url || '';
      const urlVariables = placeholders(item.url);
      if (urlVariables.length && (urlVariables.length !== 1 || urlVariables[0] !== '1' || !/\{\{\s*1\s*\}\}$/.test(item.url || ''))) errors.push(`${title}: a website URL can use only {{1}} at the end.`);
      if (urlVariables.length) {
        if (!item.example) errors.push(`${title}: enter a sample URL with the variable filled in.`);
        result.example = [item.example || ''];
      }
    } else if (type === 'PHONE_NUMBER') {
      if (!/^\+?[0-9]{6,20}$/.test(item.phone_number || '')) errors.push(`${title}: enter a phone number with country code.`);
      result.phone_number = item.phone_number || '';
    }
    return result;
  });
}

function makeHeader(form, errors) {
  const type = form.headerType;
  if (type === 'NONE') return null;
  if (type === 'LOCATION' || type === 'PRODUCT') return { type: 'HEADER', format: type };
  if (type === 'TEXT') {
    if (!form.headerText.trim() || form.headerText.length > 60) errors.push('Enter header text up to 60 characters.');
    const result = { type: 'HEADER', format: 'TEXT', text: form.headerText.trim() };
    const example = textExample(form.headerText, form.headerExamples, 'header', errors, 'Header');
    if (example) result.example = example;
    return result;
  }
  if (!['IMAGE', 'VIDEO', 'DOCUMENT'].includes(type)) { errors.push('Choose a valid header type.'); return null; }
  if (!form.mediaHandle) errors.push('Upload header media before submitting.');
  return { type: 'HEADER', format: type, example: { header_handle: [form.mediaHandle || ''] } };
}

export function buildMarketingPayload(form) {
  const errors = [];
  if (!/^[a-z0-9_]{1,512}$/.test(form.name || '')) errors.push('Enter a unique lowercase template name using letters, numbers, and underscores.');
  if (!/^[a-z]{2,3}(?:_[A-Z]{2})?$/.test(form.language || '')) errors.push('Enter a valid language code, such as en_US.');
  const bodyLimit = form.format === 'limited_offer' ? 600 : 1024;
  if (!form.body?.trim() || form.body.length > bodyLimit) errors.push(`Enter message text up to ${bodyLimit} characters.`);
  if (form.footer?.length > 60) errors.push('Footer must be 60 characters or fewer.');

  const components = [];
  const header = makeHeader(form, errors);
  if (header) components.push(header);
  if (form.format === 'limited_offer') {
    if (!form.offerText?.trim() || form.offerText.trim().length > 16) errors.push('Enter an offer label up to 16 characters.');
    components.push({ type: 'LIMITED_TIME_OFFER', limited_time_offer: { text: form.offerText?.trim() || '', has_expiration: Boolean(form.offerExpires) } });
  }
  const message = { type: 'BODY', text: (form.body || '').trim() };
  const bodyExample = textExample(form.body, form.examples, 'body', errors, 'Body');
  if (bodyExample) message.example = bodyExample;
  components.push(message);
  if (form.footer?.trim() && !['call_permission', 'media_carousel', 'product_carousel'].includes(form.format)) components.push({ type: 'FOOTER', text: form.footer.trim() });

  if (form.format === 'media_carousel' || form.format === 'product_carousel') {
    if (form.cards.length < 2 || form.cards.length > 10) errors.push('A carousel needs 2 to 10 cards.');
    const product = form.format === 'product_carousel';
    const cards = form.cards.map((item, index) => {
      const cardComponents = [];
      if (product) cardComponents.push({ type: 'HEADER', format: 'PRODUCT' });
      else {
        if (!['IMAGE', 'VIDEO'].includes(item.headerType)) errors.push(`Card ${index + 1}: choose image or video.`);
        if (!item.mediaHandle) errors.push(`Card ${index + 1}: upload media.`);
        cardComponents.push({ type: 'HEADER', format: item.headerType, example: { header_handle: [item.mediaHandle || ''] } });
      }
      if (item.body?.trim()) {
        if (item.body.length > 160) errors.push(`Card ${index + 1}: card text must be 160 characters or fewer.`);
        const cardBody = { type: 'BODY', text: item.body.trim() };
        const example = textExample(item.body, item.examples, 'body', errors, `Card ${index + 1}`);
        if (example) cardBody.example = example;
        cardComponents.push(cardBody);
      }
      if (!item.buttons.length || item.buttons.length > 2) errors.push(`Card ${index + 1}: add 1 or 2 buttons.`);
      cardComponents.push({ type: 'BUTTONS', buttons: makeButtons(item.buttons, errors, `Card ${index + 1}`) });
      return { components: cardComponents };
    });
    if (form.cards.some(item => Boolean(item.body?.trim()) !== Boolean(form.cards[0]?.body?.trim()))) errors.push('Either every carousel card must have body text, or none may have it.');
    const signature = item => item.buttons.map(button => button.type).join(',');
    if (form.cards.some(item => item.headerType !== form.cards[0]?.headerType || signature(item) !== signature(form.cards[0]))) errors.push('All carousel cards must use matching media and button types.');
    components.push({ type: 'CAROUSEL', cards });
  } else if (form.format === 'call_permission') {
    components.push({ type: 'CALL_PERMISSION_REQUEST' });
  } else if (form.buttons.length) {
    if (form.buttons.length > 10) errors.push('Add no more than 10 buttons.');
    components.push({ type: 'BUTTONS', buttons: makeButtons(form.buttons, errors, 'Template') });
  }

  const formatSpecific = {
    coupon: 'COPY_CODE', limited_offer: 'COPY_CODE', catalog: 'CATALOG', multi_product: 'MPM', single_product: 'SPM',
  };
  const requiredButton = formatSpecific[form.format];
  if (requiredButton && !form.buttons.some(item => item.type === requiredButton)) errors.push(`This format needs a ${requiredButton.replace(/_/g, ' ').toLowerCase()} button.`);
  if (form.format === 'coupon' && form.buttons.filter(item => item.type === 'COPY_CODE').length !== 1) errors.push('Coupon templates need exactly one copy-code button.');
  if (form.format === 'limited_offer' && form.buttons.filter(item => item.type === 'URL').length !== 1) errors.push('Limited-time offers need a website button.');
  if (form.format === 'location' && form.headerType !== 'LOCATION') errors.push('Location templates need a location header.');
  if (form.format === 'multi_product' && form.headerType === 'NONE') errors.push('Multi-product templates need a header.');
  if (form.format === 'single_product' && form.headerType !== 'PRODUCT') errors.push('Single-product templates need a product header.');
  if (form.format === 'limited_offer' && !['IMAGE', 'VIDEO'].includes(form.headerType)) errors.push('Limited-time offers need an image or video header.');
  const numeric = [form.body, form.headerText, ...form.cards.map(item => item.body)].some(text => placeholders(text).some(key => /^\d+$/.test(key)));
  const named = [form.body, form.headerText, ...form.cards.map(item => item.body)].some(text => placeholders(text).some(key => !/^\d+$/.test(key)));
  if (numeric && named) errors.push('Use either numbered or named variables throughout the template, not both.');
  const payload = { name: form.name || '', language: form.language || '', category: 'MARKETING', ...(named ? { parameter_format: 'named' } : {}), components };
  return { payload, errors };
}

export function newMarketingCard(format) { return card(format === 'product_carousel' ? 'PRODUCT' : 'IMAGE'); }
export function newMarketingButton(type = 'QUICK_REPLY') { return button(type); }
